// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

// Local imports
import { calcChargeableWeight, calcWeight } from '../../CalcWeight.js';
import { opportunitySellAndCost } from '../../utils/opportunityFinancials.js';
import currencyToText from '../../CurrencyToText.js';

dayjs.extend(isoWeek);

const toDayjsUnit = (p) => p === 'week' ? 'isoWeek' : p;

function filterOpportunities({ opportunities, tradeline, timespanJump = 0 }) {
    const timespan = getTradelineTimespan({ tradeline, timespanJump });
    const filteredOpportunities = opportunities
        .filter(o => !tradeline.accountId || (o.account?.id === tradeline.accountId))
        .filter(o => !tradeline.transportationMethod || (o.transportationMethod === tradeline.transportationMethod))
        .filter(o => !timespan || (o.created >= timespan.start && o.created <= timespan.end))
        .filter(o => !tradeline.opportunitiesOwner || (o.owner === tradeline.opportunitiesOwner))
        .filter(o => {
            if (_.isEmpty(tradeline.routes)) return true;
            return _.some(o.routes, route => _.some(tradeline.routes, r => {
                if (r.source && route.source) {
                    const exactMatch = r.source.id === route.source.id;
                    // Country-level tradeline route matches any port/city in that country
                    const countryMatch = r.source.type === 'country' && route.source.countryCode === r.source.countryCode;
                    if (!exactMatch && !countryMatch) return false;
                }
                if (r.destination && route.destination) {
                    const exactMatch = r.destination.id === route.destination.id;
                    const countryMatch = r.destination.type === 'country' && route.destination.countryCode === r.destination.countryCode;
                    if (!exactMatch && !countryMatch) return false;
                }
                return true;
            }));
        });
    
    return filteredOpportunities;
}

function getTradelinesStatus({ opportunities, exchangeRates, tradeline, timespanJump = 0, organization }) {
    const filteredOpportunities = filterOpportunities({ opportunities, tradeline, timespanJump })
    const won = filteredOpportunities.filter(o => o.status === 'completed');

    const numShipments = won.length;
    const numContainers = won.reduce((acc, o) => acc + _.sumBy(o.products, p => {
        const qty = p.quantity || 1;
        const pType = (p.type || '').toLowerCase();
        if (pType.includes('40')) return qty * 2; // 40' = 2 TEUs
        return qty; // 20' or anything else = 1 TEU
    }), 0);
    const totalChargeableWeight = won.reduce((acc, o) => {
        const weights = _.compact(o.products.map(p => calcChargeableWeight({ product: p, transportationMethod: o.transportationMethod, allProducts: o.products, round: organization.airRoundChargeableWeight })));
        weights.forEach(w => acc[w.postfix] = (acc[w.postfix] || 0) + w.val);
        return acc;
    }, {});
    const totalGrossWeight = won.reduce((acc, o) => {
        const weights = _.compact(o.products.map(p => calcWeight({ product: p })));
        weights.forEach(w => acc[w.postfix] = (acc[w.postfix] || 0) + w.val);
        return acc;
    }, {});
    // Each won quote's own Sell and Cost (opportunitySellAndCost): in the organization's currency, at
    // the exchange rates the quote was priced at, as on the quote's page and in Quotation Analytics.
    // Kept as { currency: amount } maps, converted to a goal's currency below.
    const orgCurrency = organization?.defaults?.currency || 'USD';
    const totalRevenue = {};
    const totalExpenses = {};
    won.forEach(o => {
        const fin = opportunitySellAndCost({ opportunity: o, exchangeRates, organization });
        if (!fin) return;
        if (isFinite(fin.sell)) totalRevenue[orgCurrency] = (totalRevenue[orgCurrency] || 0) + fin.sell;
        if (isFinite(fin.cost)) totalExpenses[orgCurrency] = (totalExpenses[orgCurrency] || 0) + fin.cost;
    });
    const totalProfit = _.union(_.keys(totalRevenue), _.keys(totalExpenses)).reduce((acc, currency) => {
        acc[currency] = (totalRevenue[currency] || 0) - (totalExpenses[currency] || 0);
        return acc;
    }, {});

    const results = {};
    tradeline.goals.forEach(g => {
        if (g.type === 'units') {
            results[g.id] = {
                value: numContainers || 0,
                goal: g.value,
                progress: (numContainers || 0) / g.value,
            }
        }
        if (g.type === 'shipments') {
            results[g.id] = {
                value: numShipments || 0,
                goal: g.value,
                progress: (numShipments || 0) / g.value,
            }
        }
        if (g.type === 'chargeable-weight') {
            const baseWeight = g.kgOrLbs;
            const totalChargeableWeightValue = _(totalChargeableWeight).toPairs().sumBy(([postfix, val]) => {
                return val * WEIGHT_CONVERSION[`${baseWeight}|${postfix}`];
            });
            results[g.id] = {
                value: totalChargeableWeightValue || 0,
                goal: g.value,
                progress: (totalChargeableWeightValue || 0) / g.value,
            }
        }
        if (g.type === 'gross-weight') {
            const baseWeight = g.kgOrLbs;
            const totalGrossWeightValue = _(totalGrossWeight).toPairs().sumBy(([postfix, val]) => {
                return val * WEIGHT_CONVERSION[`${baseWeight}|${postfix}`];
            });
            results[g.id] = {
                value: totalGrossWeightValue || 0,
                goal: g.value,
                progress: (totalGrossWeightValue || 0) / g.value,
            }
        }
        if (g.type === 'revenue') {
            const totalRevenueValue = _(totalRevenue).toPairs().sumBy(([currency, val]) => val * exchangeRates[g.currency] / exchangeRates[currency]);
            results[g.id] = {
                value: totalRevenueValue || 0,
                goal: g.value,
                progress: (totalRevenueValue || 0) / g.value,
            }
        }
        if (g.type === 'profit') {
            const totalProfitValue = _(totalProfit).toPairs().sumBy(([currency, val]) => val * exchangeRates[g.currency] / exchangeRates[currency]);
            results[g.id] = {
                value: totalProfitValue || 0,
                goal: g.value,
                progress: (totalProfitValue || 0) / g.value,
            }
        }
    });

    const aggregatedProgress = _.sumBy(_.values(results), r => r.progress || 0) / _.values(results).length;
    const status = (() => {
        if (aggregatedProgress >= 1) return 'completed';
        const timespan = getTradelineTimespan({ tradeline, timespanJump });
        if (timespan && timespan.end < new Date().getTime()) return 'failed';
        return 'inprogress';
    })();

    return { 
        opportunities: filteredOpportunities, 
        wonOpportunities: won,
        accumulatedValues: {
            numShipments,
            numContainers,
            totalChargeableWeight,
            totalGrossWeight,
            totalRevenue,
            totalExpenses,
            totalProfit,
        },
        results,
        aggregated: {
            status,
            progress: aggregatedProgress,
        },
    }
}

function getTradelineTimespan({ tradeline, timespanJump = 0 }) {
    if (tradeline.timespanType === 'recurring') {
        return {
            start: dayjs().startOf(toDayjsUnit(tradeline.recurringPeriod)).add(timespanJump, tradeline.recurringPeriod).valueOf(),
            end: dayjs().endOf(toDayjsUnit(tradeline.recurringPeriod)).add(timespanJump, tradeline.recurringPeriod).valueOf(),
        }
    }
    return {
        start: tradeline.timespan?.start || dayjs().startOf('day').valueOf(),
        end: tradeline.timespan?.end || dayjs().endOf('day').valueOf(),
    }
}

function goalToText({ goal, transportationMethod, short, html }) {
    const valueStr = (() => {
        if (goal.type === 'units') return `${goal.value} ${transportationMethod === 'air' ? 'freight units' : 'TEUs'}`;
        if (goal.type === 'shipments') return `${goal.value} shipments`;
        if (goal.type === 'chargeable-weight') return `${goal.value} ${goal.kgOrLbs}`;
        if (goal.type === 'gross-weight') return `${goal.value} ${goal.kgOrLbs}`;
        if (goal.type === 'revenue') return currencyToText(goal.currency, true, goal.value);
        if (goal.type === 'profit') return currencyToText(goal.currency, true, goal.value);
    })();

    if (short) {
        if (goal.type === 'units') return <div><div style={{ fontWeight: 800 }}>{goal.value}</div> {transportationMethod === 'air' ? 'Freight Units' : 'TEUs'}</div>;
        if (goal.type === 'shipments') return <div><div style={{ fontWeight: 800 }}>{goal.value}</div> Shipments</div>;
        if (goal.type === 'chargeable-weight') return <div><div style={{ fontWeight: 800 }}>{valueStr}</div> Chargeable Weight</div>;
        if (goal.type === 'gross-weight') return <div><div style={{ fontWeight: 800 }}>{valueStr}</div> Gross Weight</div>;
        if (goal.type === 'revenue') return <div><div style={{ fontWeight: 800 }}>{valueStr}</div> Revenue</div>;
        if (goal.type === 'profit') return <div><div style={{ fontWeight: 800 }}>{valueStr}</div> Profit</div>;
        return valueStr;
    }

    if (html) {
        const label = (GOAL_TYPES_SHORT[transportationMethod] || GOAL_TYPES_SHORT.sea).find(g2 => g2.type === goal.type)?.label || goal.type;
        return label + ': <b>' + valueStr + '</b>';
    }

    const label = (GOAL_TYPES[transportationMethod] || GOAL_TYPES.sea).find(g2 => g2.type === goal.type)?.label || goal.type;
    return label + ': ' + valueStr;
}

const GOAL_TYPES =
{
    air: [
        { type: 'units', label: 'Number of Freight Units' },
        { type: 'shipments', label: 'Number of Shipments' },
        { type: 'chargeable-weight', label: 'Total Chargeable Weight' },
        { type: 'gross-weight', label: 'Total Gross Weight' },
        { type: 'revenue', label: 'Total Revenue' },
        { type: 'profit', label: 'Total Profit' },
    ],
    sea: [
        { type: 'units', label: 'Number of TEUs' },
        { type: 'shipments', label: 'Number of Shipments' },
        { type: 'chargeable-weight', label: 'Total Chargeable Weight' },
        { type: 'gross-weight', label: 'Total Gross Weight' },
        { type: 'revenue', label: 'Total Revenue' },
        { type: 'profit', label: 'Total Profit' },
    ],
    land: [
        { type: 'units', label: 'Number of TEUs/Freight Units' },
        { type: 'shipments', label: 'Number of Shipments' },
        { type: 'chargeable-weight', label: 'Total Chargeable Weight' },
        { type: 'gross-weight', label: 'Total Gross Weight' },
        { type: 'revenue', label: 'Total Revenue' },
        { type: 'profit', label: 'Total Profit' },
    ]
}

const GOAL_TYPES_SHORT =
{
    air: [
        { type: 'units', label: 'Freight Units' },
        { type: 'shipments', label: 'Shipments' },
        { type: 'chargeable-weight', label: 'Chargeable Weight' },
        { type: 'gross-weight', label: 'Gross Weight' },
        { type: 'revenue', label: 'Total Revenue' },
        { type: 'profit', label: 'Total Profit' },
    ],
    sea: [
        { type: 'units', label: 'TEUs' },
        { type: 'shipments', label: 'Shipments' },
        { type: 'chargeable-weight', label: 'Chargeable Weight' },
        { type: 'gross-weight', label: 'Gross Weight' },
        { type: 'revenue', label: 'Total Revenue' },
        { type: 'profit', label: 'Total Profit' },
    ],
    land: [
        { type: 'units', label: 'TEUs/Freight Units' },
        { type: 'shipments', label: 'Shipments' },
        { type: 'chargeable-weight', label: 'Chargeable Weight' },
        { type: 'gross-weight', label: 'Gross Weight' },
        { type: 'revenue', label: 'Total Revenue' },
        { type: 'profit', label: 'Total Profit' },
    ]
}

export { getTradelinesStatus, filterOpportunities, getTradelineTimespan, goalToText, GOAL_TYPES }

const WEIGHT_CONVERSION = {
    'kg|kg': 1,
    'kg|lbs': 2.20462,
    'lbs|kg': 0.453592,
    'lbs|lbs': 1,
}
